import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Typography } from '@mui/material';
import { FC } from 'react';
import { Resolver, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from 'src/components/button';
import { ControlledField } from 'src/components/controlled-field';
import { selectBasket } from 'src/store/slices/basket-slice';
import { selectUserChoices } from 'src/store/slices/payment-plan-slice';

import { RoommateFormValues, roommateDataKeys } from './constants';
import { roommateDetailsSchema } from './roommate-details-schema';
import { styles } from './styles';
import { RoommateFormProps } from './types';

export const RoommateForm: FC<RoommateFormProps> = ({ onFormSubmit, loading }) => {
    const { t } = useTranslation();
    const basket = useSelector(selectBasket);

    // Get the number of students from Redux
    const userChoices = useSelector(selectUserChoices);
    const { numberOfStudents } = userChoices;

    const generateDefaultRoommates = () => {
        const roommates = basket.room_mates;
        if (!roommates || !roommates.length) {
            return Array.from({ length: numberOfStudents - 1 }, () => ({
                first_name: '',
                last_name: '',
                email: '',
            }));
        }

        const result = Array.from({ length: numberOfStudents - 1 }, (element, index) => ({
            first_name: roommates[index].first_name ?? '',
            last_name: roommates[index].last_name ?? '',
            email: roommates[index].email ?? '',
        }));

        return result;
    };

    const { control, handleSubmit } = useForm<RoommateFormValues>({
        resolver: yupResolver(roommateDetailsSchema) as Resolver<RoommateFormValues>,
        defaultValues: { roommates: generateDefaultRoommates() },
        mode: 'all',
    });

    const { fields } = useFieldArray({
        control,
        name: 'roommates',
    });

    const onSubmit = handleSubmit(onFormSubmit);

    return (
        <Box css={styles.roommateDetailsContainer}>
            {fields.map((field, index) => (
                <Box key={field.id}>
                    <Box css={styles.roommateHeader}>
                        <Typography variant="h3">
                            {t('roommates_form_header', {
                                number: index + 1,
                            })}
                        </Typography>
                    </Box>
                    <Box css={styles.inputGroupWrapper}>
                        <ControlledField
                            name={`roommates[${index}].${roommateDataKeys.first_name}`}
                            control={control}
                            label={t('your_details_form_first_name_label')}
                            placeholder={t('your_details_form_first_name_placeholder')}
                            type="text"
                        />
                        <ControlledField
                            name={`roommates[${index}].${roommateDataKeys.last_name}`}
                            control={control}
                            type="text"
                            label={t('your_details_form_last_name_label')}
                            placeholder={t('your_details_form_last_name_placeholder')}
                        />
                    </Box>
                    <Box css={styles.email}>
                        <ControlledField
                            name={`roommates[${index}].${roommateDataKeys.email}`}
                            control={control}
                            type="text"
                            label={t('your_details_form_email_label')}
                            placeholder={t('your_details_form_email_placeholder')}
                        />
                    </Box>
                </Box>
            ))}

            <Button variant="contained" onClick={onSubmit} css={styles.cta} loading={loading}>
                {t('roommates_form_cta')}
            </Button>
        </Box>
    );
};
